import { useState } from 'react';
import { Button } from '../ui/Button';
import { trpc } from '../../lib/trpc';
import { FormSection } from '../ui/FormSection';

interface PreferenceFieldViewProps {
  label: string;
  value: string | null | undefined;
  emptyText: string;
}

const PreferenceFieldView = ({ label, value, emptyText }: PreferenceFieldViewProps) => (
  <div>
    <dt className="text-sm text-gray-500">{label}</dt>
    {value ? (
      <dd className="mt-1 text-sm text-gray-900 whitespace-pre-line">{value}</dd>
    ) : (
      <dd className="mt-1 text-sm text-gray-400 italic">{emptyText}</dd>
    )}
  </div>
);

interface PreferencesData {
  winePreferences: string | null;
  wineDislikes: string | null;
  regionPreference: string | null;
  venuePreferences: string | null;
}

interface PreferencesTabProps {
  preferences: PreferencesData;
}

const FIELDS = [
  {
    key: 'winePreferences' as const,
    label: 'Wine Preferences',
    emptyText: 'No wine preferences set',
    placeholder:
      'Describe the wines you enjoy — flavors, styles, varietals, body, sweetness level. For example: "I love bold, full-bodied reds like Cabernet Sauvignon and Malbec. I enjoy dark fruit flavors, vanilla oak, and a long finish."',
  },
  {
    key: 'wineDislikes' as const,
    label: 'Wine Dislikes',
    emptyText: 'No wine dislikes set',
    placeholder:
      'Describe wines or characteristics you tend to avoid. For example: "I don\'t enjoy overly sweet wines or heavy oak. High-tannin wines give me headaches. I generally skip Riesling and Moscato."',
  },
  {
    key: 'regionPreference' as const,
    label: 'Region Preferences',
    emptyText: 'No region preferences set',
    placeholder:
      'Which wine regions do you gravitate toward? For example: "I love Napa Valley Cabs and Willamette Valley Pinot Noirs. I\'m curious about wines from the Rhône Valley and Mendoza, Argentina."',
  },
  {
    key: 'venuePreferences' as const,
    label: 'Venue Preferences',
    emptyText: 'No venue preferences set',
    placeholder:
      'What do you look for in a winery or tasting room? For example: "I prefer intimate, appointment-only tastings with knowledgeable staff. Outdoor seating is a must. I like dog-friendly wineries with food pairing options."',
  },
];

export const PreferencesTab = ({ preferences }: PreferencesTabProps) => {
  const [isEditing, setIsEditing] = useState(false);

  const [winePreferences, setWinePreferences] = useState(preferences.winePreferences || '');
  const [wineDislikes, setWineDislikes] = useState(preferences.wineDislikes || '');
  const [regionPreference, setRegionPreference] = useState(preferences.regionPreference || '');
  const [venuePreferences, setVenuePreferences] = useState(preferences.venuePreferences || '');

  const [submitError, setSubmitError] = useState<string | null>(null);

  const formState: Record<string, { value: string; setter: (v: string) => void }> = {
    winePreferences: { value: winePreferences, setter: setWinePreferences },
    wineDislikes: { value: wineDislikes, setter: setWineDislikes },
    regionPreference: { value: regionPreference, setter: setRegionPreference },
    venuePreferences: { value: venuePreferences, setter: setVenuePreferences },
  };

  const utils = trpc.useUtils();
  const updateProfile = trpc.user.updateProfile.useMutation({
    onSuccess: () => {
      utils.user.getMe.invalidate();
      setIsEditing(false);
      setSubmitError(null);
    },
    onError: (error) => {
      setSubmitError(error.message);
    },
  });

  const resetForm = () => {
    setWinePreferences(preferences.winePreferences || '');
    setWineDislikes(preferences.wineDislikes || '');
    setRegionPreference(preferences.regionPreference || '');
    setVenuePreferences(preferences.venuePreferences || '');
    setSubmitError(null);
  };

  const handleCancel = () => {
    resetForm();
    setIsEditing(false);
  };

  const handleSave = () => {
    updateProfile.mutate({
      winePreferences,
      wineDislikes,
      regionPreference,
      venuePreferences,
    });
  };

  if (isEditing) {
    return (
      <div>
        <FormSection
          title="Edit Preferences"
          contentClassName="space-y-6"
          actions={
            <>
              {submitError && (
                <p className="text-sm text-red-600 self-center mr-2">{submitError}</p>
              )}
              <Button variant="outline" size="sm" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSave}
                disabled={updateProfile.isPending}
                loading={updateProfile.isPending}
              >
                Save
              </Button>
            </>
          }
        >
          {FIELDS.map((field) => (
            <div key={field.key}>
              <label
                htmlFor={`pref-${field.key}`}
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {field.label}
              </label>
              <textarea
                id={`pref-${field.key}`}
                value={formState[field.key].value}
                onChange={(e) => formState[field.key].setter(e.target.value)}
                rows={4}
                maxLength={2000}
                disabled={updateProfile.isPending}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-info-500 disabled:opacity-50"
                placeholder={field.placeholder}
              />
              <p className="text-xs text-gray-400 mt-1">
                {formState[field.key].value.length}/2000
              </p>
            </div>
          ))}
        </FormSection>
      </div>
    );
  }

  return (
    <div>
      <FormSection
        title="Wine Preferences"
        actions={
          <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
            Edit Preferences
          </Button>
        }
      >
        <dl className="space-y-5">
          {FIELDS.map((field) => (
            <PreferenceFieldView
              key={field.key}
              label={field.label}
              value={preferences[field.key]}
              emptyText={field.emptyText}
            />
          ))}
        </dl>
      </FormSection>
    </div>
  );
};
